// card-gallery-swapper.js
// Swatch swapping that preserves responsive behavior by rebuilding srcset
// using the EXISTING candidates from the initial srcset (no reliance on data-widths).

(() => {
  // --- Disable hover images on touch devices (mobile/tablet) ---
  const IS_TOUCH = matchMedia('(hover: none), (pointer: coarse)').matches;

  if (IS_TOUCH) {
    const removeHoverImg = (root = document) => {
      root.querySelectorAll('.card-gallery__img--hover').forEach((img) => {
        img.remove();
      });
    };

    removeHoverImg();

    const mo = new MutationObserver((muts) => {
      for (const m of muts) {
        for (const n of m.addedNodes) {
          if (n.nodeType === 1) removeHoverImg(n);
        }
      }
    });

    mo.observe(document.documentElement, { childList: true, subtree: true });
  }

  const parseMap = (gallery) => {
    const script = gallery.querySelector('.card-gallery__media-map');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent || '{}');
    } catch {
      return null;
    }
  };

  const buildUrl = (template, width) => template.replace('width=1', `width=${width}`);

  const getCardGallery = (el) => {
    const productCard = el.closest('product-card');
    if (!productCard) return null;
    return productCard.querySelector('.card-gallery__media[data-card-gallery-mode="fast"]');
  };

  const cachePrimaryBaseline = (gallery, img) => {
    if (gallery.__baselineCached) return;
    gallery.__baselineCached = true;

    const srcset = img.getAttribute('srcset') || '';
    const sizes = img.getAttribute('sizes') || '';
    const src = img.getAttribute('src') || '';

    // Extract widths from existing srcset candidates (e.g. "...width=240 240w")
    const widths = srcset
      .split(',')
      .map((s) => s.trim())
      .map((cand) => {
        const m = cand.match(/width=(\d+)/);
        return m ? parseInt(m[1], 10) : null;
      })
      .filter((n) => Number.isFinite(n))
      .sort((a, b) => a - b);

    gallery.__baseline = { srcset, sizes, src, widths: widths.length ? widths : [240] };
  };

  const applyMediaId = (gallery, mediaId) => {
    const map = gallery.__mediaMap || (gallery.__mediaMap = parseMap(gallery));
    const template = map?.[mediaId]?.t;
    if (!template) return;

    const primary = gallery.querySelector('.card-gallery__img--primary');
    if (!primary) return;

    cachePrimaryBaseline(gallery, primary);

    const { sizes, widths } = gallery.__baseline;

    // Rebuild srcset using the SAME widths as baseline, just swapping URLs
    const newSrcset = widths.map((w) => `${buildUrl(template, w)} ${w}w`).join(', ');

    primary.setAttribute('srcset', newSrcset);
    if (sizes) primary.setAttribute('sizes', sizes);

    // Ensure src matches the smallest candidate (or keep current behavior if you prefer)
    primary.setAttribute('src', buildUrl(template, widths[0]));

    primary.dataset.mediaId = String(mediaId);
    primary.decoding = 'async';
  };

  const resetToSelectedOrDefault = (gallery) => {
    const productCard = gallery.closest('product-card');
    if (!productCard) return;

    const checked = productCard.querySelector('input[type="radio"][data-option-media-id]:checked');
    const targetId = checked?.dataset.optionMediaId || gallery.getAttribute('data-default-media-id');
    if (targetId) {
      applyMediaId(gallery, targetId);
      return;
    }

    // Restore baseline if no selection
    const primary = gallery.querySelector('.card-gallery__img--primary');
    if (!primary || !gallery.__baseline) return;

    const { srcset, sizes, src } = gallery.__baseline;
    if (srcset) primary.setAttribute('srcset', srcset);
    if (sizes) primary.setAttribute('sizes', sizes);
    if (src) primary.setAttribute('src', src);
  };

  let hoverTimer = 0;

  document.addEventListener(
    'pointerover',
    (e) => {
      if (e.pointerType !== 'mouse') return;
      const label = e.target?.closest?.('label[data-media-id]');
      if (!label) return;

      const gallery = getCardGallery(label);
      if (!gallery) return;

      const mediaId = label.getAttribute('data-media-id');
      if (!mediaId) return;

      clearTimeout(hoverTimer);
      hoverTimer = window.setTimeout(() => applyMediaId(gallery, mediaId), 90);
    },
    { passive: true, capture: true }
  );

  document.addEventListener(
    'pointerout',
    (e) => {
      if (e.pointerType !== 'mouse') return;
      const label = e.target?.closest?.('label[data-media-id]');
      if (!label) return;

      const gallery = getCardGallery(label);
      if (!gallery) return;

      clearTimeout(hoverTimer);
      window.setTimeout(() => resetToSelectedOrDefault(gallery), 60);
    },
    { passive: true, capture: true }
  );

  document.addEventListener(
    'change',
    (e) => {
      const input = e.target;
      if (!(input instanceof HTMLInputElement)) return;
      if (input.type !== 'radio') return;

      const mediaId = input.dataset.optionMediaId;
      if (!mediaId) return;

      const gallery = getCardGallery(input);
      if (!gallery) return;

      applyMediaId(gallery, mediaId);
    },
    { passive: true, capture: true }
  );

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.card-gallery__media[data-card-gallery-mode="fast"]').forEach((gallery) => {
      resetToSelectedOrDefault(gallery);
    });
  });
})();